import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FeatureProvider } from './contexts/FeatureContext';
import { useState, useEffect } from 'react';
import AppLayout from './components/layout/AppLayout';
import LoginPage from './components/auth/LoginPage';
import RegisterPage from './components/auth/RegisterPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import PartnersPage from './pages/partners/PartnersPage';
import PartnerDetailPage from './pages/partners/PartnerDetailPage';
import ItemsPage from './pages/items/ItemsPage';
import OrdersPage from './pages/orders/OrdersPage';
import DeliveryNotesPage from './pages/delivery-notes/DeliveryNotesPage';
import InvoicesPage from './pages/invoices/InvoicesPage';
import IncomingInvoicesPage from './pages/incoming-invoices/IncomingInvoicesPage';
import ReceivablesPage from './pages/receivables/ReceivablesPage';
import ProjectsPage from './pages/projects/ProjectsPage';
import ProcurementPage from './pages/procurement/ProcurementPage';
import TimeTrackingPage from './pages/time-tracking/TimeTrackingPage';
import DocumentsPage from './pages/documents/DocumentsPage';
import AllocationsPage from './pages/allocations/AllocationsPage';
import RecurringInvoicesPage from './pages/recurring-invoices/RecurringInvoicesPage';
import SettingsPage from './pages/settings/SettingsPage';
import OnboardingPlanStep from './pages/onboarding/OnboardingPlanStep';
import BillingPage from './pages/settings/BillingPage';
import NavSettingsPage from './pages/settings/NavSettingsPage';
import CompanySettingsPage from './pages/settings/CompanySettingsPage';
import UsersPage from './pages/settings/UsersPage';
import AuditPage from './pages/settings/AuditPage';
import NotificationsSettingsPage from './pages/settings/NotificationsSettingsPage';
import AccountantDashboard from './pages/accountant/AccountantDashboard';
import DesignSystemShowcase from './pages/design-system/DesignSystemShowcase';
import ScannerPage from './pages/scanner/ScannerPage';
import CashReceiptsPage from './pages/cash-register/CashReceiptsPage';
import QuotesPage from './pages/quotes/QuotesPage';
import WarehousePage from './pages/warehouse/WarehousePage';
import BankMatchingPage from './pages/bank-matching/BankMatchingPage';
import ImportOnboardingPage from './pages/import-onboarding/ImportOnboardingPage';
import PlatformAdminPage from './pages/platform-admin/PlatformAdminPage';
import AdminConsoleApp from './pages/platform-admin/AdminConsoleApp';
import ReportsPage from './pages/reports/ReportsPage';
import BookingAdminPage from './pages/booking/BookingAdminPage';
import PublicTimesheetPage from './pages/public/PublicTimesheetPage';
import TenantSwitcherPage from './pages/tenant-switcher/TenantSwitcherPage';
import NetworkPage from './pages/network/NetworkPage';
import NetworkCatalogPage from './pages/network/NetworkCatalogPage';
import { NotFoundPage, ForbiddenPage, ServerErrorPage } from './components/ErrorPage';
import OnboardingCompanyStep from './pages/onboarding/OnboardingCompanyStep';
import OnboardingNavStep from './pages/onboarding/OnboardingNavStep';
import OnboardingConfirmStep from './pages/onboarding/OnboardingConfirmStep';
import { get } from './lib/api';
import type { ReactNode } from 'react';

const SETUP_REDIRECT: Record<string, string> = {
  account_only: '/onboarding/company',
  company_pending: '/onboarding/company',
  nav_pending: '/onboarding/nav',
  plan_pending: '/onboarding/plan',
  trial_pending: '/onboarding/confirm',
};

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Betöltés...</div>;
  if (!user) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function SetupGuard({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  const [setupState, setSetupState] = useState<string | null>(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!user) return;
    get<{ setup_state: string }>('/setup-state')
      .then(d => { setSetupState(d.setup_state); setChecked(true); })
      .catch(() => { setSetupState('completed'); setChecked(true); });
  }, [user]);

  if (!checked) return <div style={{ padding: 40, textAlign: 'center' }}>Betöltés...</div>;

  const isOnboarding = location.pathname.startsWith('/onboarding');
  if (setupState && setupState !== 'completed' && !isOnboarding) {
    const redirectTo = SETUP_REDIRECT[setupState] || '/onboarding/company';
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

function GuestRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function LandingOrDashboard() {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Betöltés...</div>;
  if (user) return <Navigate to="/dashboard" replace />;
  return <Navigate to="/bejelentkezes" replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <FeatureProvider>
        <Routes>
          <Route path="/" element={<LandingOrDashboard />} />

          {/* Publikus (auth nélküli) */}
          <Route path="/bejelentkezes" element={<GuestRoute><LoginPage /></GuestRoute>} />
          <Route path="/regisztracio" element={<GuestRoute><RegisterPage /></GuestRoute>} />
          <Route path="/public/idonaplo/:token" element={<PublicTimesheetPage />} />

          {/* Cég váltás (védett, AppLayout és SetupGuard nélkül) */}
          <Route path="/cegvaltas" element={<ProtectedRoute><TenantSwitcherPage /></ProtectedRoute>} />

          {/* Onboarding (védett, de setup-guard nélkül) */}
          <Route path="/onboarding/company" element={<ProtectedRoute><OnboardingCompanyStep /></ProtectedRoute>} />
          <Route path="/onboarding/nav" element={<ProtectedRoute><OnboardingNavStep /></ProtectedRoute>} />
          <Route path="/onboarding/plan" element={<ProtectedRoute><OnboardingPlanStep /></ProtectedRoute>} />
          <Route path="/onboarding/confirm" element={<ProtectedRoute><OnboardingConfirmStep /></ProtectedRoute>} />

          {/* App oldalak (védett + setup-guard) */}
          <Route element={<ProtectedRoute><SetupGuard><AppLayout /></SetupGuard></ProtectedRoute>}>
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="partnerek" element={<PartnersPage />} />
            <Route path="partnerek/:id" element={<PartnerDetailPage />} />
            <Route path="cikkek" element={<ItemsPage />} />
            <Route path="megrendelesek" element={<OrdersPage />} />
            <Route path="szallitolevelek" element={<DeliveryNotesPage />} />
            <Route path="szamlak" element={<InvoicesPage />} />
            <Route path="ismetlodo-szamlak" element={<RecurringInvoicesPage />} />
            <Route path="bejovo-szamlak" element={<IncomingInvoicesPage />} />
            <Route path="kintlevoseg" element={<ReceivablesPage />} />
            <Route path="projektek" element={<ProjectsPage />} />
            <Route path="beszerzes" element={<ProcurementPage />} />
            <Route path="halozat" element={<NetworkPage />} />
            <Route path="halozat/katalog" element={<NetworkCatalogPage />} />
            <Route path="munkaido" element={<TimeTrackingPage />} />
            <Route path="dokumentumok" element={<DocumentsPage />} />
            <Route path="beolvaso" element={<ScannerPage />} />
            <Route path="hazipenztar" element={<CashReceiptsPage />} />
            <Route path="ajanlatok" element={<QuotesPage />} />
            <Route path="raktar" element={<WarehousePage />} />
            <Route path="banki-parosito" element={<BankMatchingPage />} />
            <Route path="import" element={<ImportOnboardingPage />} />
            <Route path="riportok" element={<ReportsPage />} />
            <Route path="idopontfoglalas" element={<BookingAdminPage />} />
            <Route path="allokacio" element={<AllocationsPage />} />
            <Route path="beallitasok" element={<SettingsPage />} />
            <Route path="beallitasok/szamlazas" element={<BillingPage />} />
            <Route path="beallitasok/nav" element={<NavSettingsPage />} />
            <Route path="beallitasok/ceg" element={<CompanySettingsPage />} />
            <Route path="beallitasok/felhasznalok" element={<UsersPage />} />
            <Route path="beallitasok/audit" element={<AuditPage />} />
            <Route path="beallitasok/ertesitesek" element={<NotificationsSettingsPage />} />
          </Route>

          {/* Platform Admin (régi) */}
          <Route path="/platform-admin" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
            <Route index element={<PlatformAdminPage />} />
          </Route>

          {/* Admin Console — teljesen elkülönített realm */}
          <Route path="/admin" element={<AdminConsoleApp />} />

          {/* Accountant */}
          <Route path="/accountant-dashboard" element={<ProtectedRoute><AccountantDashboard /></ProtectedRoute>} />

          {/* Design System / UI Kit */}
          <Route path="/_design-system" element={<DesignSystemShowcase />} />
          <Route path="/ui-kit" element={<DesignSystemShowcase />} />

          <Route path="/403" element={<ForbiddenPage />} />
          <Route path="/500" element={<ServerErrorPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </FeatureProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
